import type { User } from "@pulse/api/userAPI";
import { useFieldContext } from "../../form-context";
import type { BulkParticipant } from "@pulse/api/participantApi";
import { useParticipantSearch } from "../../hooks";
import Pfp from "@pulse/features/Header/toolbar/Pfp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { useMemo } from "react";
import { ParticipantsForSurveyQueryOptions } from "@pulse/api/queryApi";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Skeleton } from "frontend-components";

export function ParticipantSelectorUser({ user }: { user: User }) {
    const { t } = useTranslation();
    const field = useFieldContext<BulkParticipant[]>();
    const { inputRef } = useParticipantSearch();
    const surveyId = useParams({
        from: "/_hub/$customerId/pulse/$pulseId/surveys/$surveyId",
    }).surveyId;
    const handleAddParticipant = (userId: number) => {
        const currentParticipants = field.state.value || [];
        if (currentParticipants.find((p) => !p.isExternal && p.userId === userId)) return;
        field.setValue([...currentParticipants, { userId, isExternal: false }]);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };
    const { data: participants } = useQuery(
        ParticipantsForSurveyQueryOptions({ surveyId: Number(surveyId) }),
    );
    const handleRemoveParticipant = (userId: number) => {
        const currentParticipants = field.state.value || [];
        field.setValue(currentParticipants.filter((p) => p.isExternal || p.userId !== userId));
    };

    const isSelected = useMemo(() => {
        return !!field.state.value?.find((u) => !u.isExternal && u.userId === user.id);
    }, [field.state.value, user.id]);

    const isAlreadyParticipant = useMemo(() => {
        return !!participants?.find((p) => p.userId === user.id);
    }, [participants, user.id]);

    return (
        <button
            type="button"
            onClick={
                isSelected
                    ? () => handleRemoveParticipant(user.id)
                    : () => handleAddParticipant(user.id)
            }
            className={`flex w-full items-center  px-4 py-2 h-14 ${
                isSelected
                    ? "bg-secondary hover:bg-secondary/50"
                    : isAlreadyParticipant
                      ? "opacity-50 bg-secondary cursor-default!"
                      : "hover:bg-secondary/50"
            }`}
            disabled={isAlreadyParticipant}
        >
            <div className="flex items-center space-x-3">
                <div className="w-8 h-8">
                    <Pfp userId={user.id} />
                </div>
                <div className="ml-3 text-left">
                    <p className="truncate w-60">{`${user.firstName} ${user.lastName}`}</p>
                    <p className="text-sm w-60 truncate">{user.email}</p>
                </div>
            </div>
            {isSelected && <FontAwesomeIcon icon={faCheck} className="text-success" />}
            {isAlreadyParticipant && (
                <span className="ml-auto text-sm italic text-on-surface/50">
                    {t("addParticipants.alreadyParticipant")}
                </span>
            )}
        </button>
    );
}

export function ParticipantSelectorLoading() {
    return (
        <div className=" flex w-full h-14 items-center px-4 py-2">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="ml-3 flex flex-col space-y-2 w-full">
                <Skeleton className="h-4 w-2/5 rounded-xl" />
                <Skeleton className="h-4 w-3/5 rounded-xl" />
            </div>
        </div>
    );
}
